/**
 * R-16 — Amendments subsection on the site Audit tab.
 */
import React, { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { useStore } from '../../../store';
import type { SiteEntity } from '../../../types/site';
import type { SiteRagReport } from '../../../types/siteRag';
import { markCitationsWithAmendments } from '../../../lib/citations/amendmentMarker';
import { formatIsoDate } from '../../../lib/referenceUrl';
import { Button } from '../../ui/button';

function truncate(text: string, max: number): string {
  const t = text.replace(/^"|"$/g, '').trim();
  return t.length <= max ? t : `${t.slice(0, max)}…`;
}

export function AmendmentsAuditPanel({
  site,
  reports,
}: {
  site: SiteEntity;
  reports: SiteRagReport[];
}) {
  const allAmendments = useStore((s: ReturnType<typeof useStore.getState>) => s.amendmentState.amendments);
  const amendments = useMemo(
    () => allAmendments.filter((a) => a.sedId === site.sedId),
    [allAmendments, site.sedId],
  );

  const rows = useMemo(() => {
    const primaryReport = reports[0];
    const markedPrimary = primaryReport
      ? markCitationsWithAmendments(primaryReport, { sedAmendments: amendments })
      : null;

    const sedRows = amendments
      .filter((a) => a.status === 'applied' || a.status === 'approved' || a.status === 'pending')
      .map((a) => {
        const section = markedPrimary?.sectionAmendments?.[a.variablePath]?.find((s: { amendmentId: string }) => s.amendmentId === a.id);
        return {
          id: a.id,
          kind: 'sed' as const,
          label: a.variableLabel || a.variablePath,
          original: String(a.oldValue ?? '—'),
          amended: String(a.newValue ?? '—'),
          by: a.createdByEmail,
          at: a.appliedAt ?? a.createdAt,
          rationale: a.reason,
          citationsAffected: section?.citationsAffected ?? [],
        };
      });

    const editRows = reports.flatMap((report) => {
      const marked = markCitationsWithAmendments(report, { sedAmendments: amendments });
      return (report.editHistory ?? []).flatMap((edit) => {
        const section = marked.sectionAmendments?.[edit.field]?.find((s: { amendmentId: string }) => s.amendmentId === edit.id);
        if (!section) return [];
        return [{
          id: edit.id,
          kind: 'report-edit' as const,
          label: edit.field,
          original: edit.originalValue,
          amended: edit.editedValue,
          by: 'User correction',
          at: edit.editedAt,
          rationale: undefined as string | undefined,
          citationsAffected: section.citationsAffected,
          reportId: report.id,
        }];
      });
    });

    return [...sedRows, ...editRows].sort((a, b) => b.at.localeCompare(a.at));
  }, [amendments, reports]);

  const citationCount = rows.reduce((n, r) => n + r.citationsAffected.length, 0);

  const jumpToReport = (reportId: string) => {
    window.location.hash = `#/sites/${site.id}/reports/${reportId}`;
  };

  return (
    <section className="space-y-3" data-testid="audit-amendments-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary">
          Amendments ({rows.length})
        </h3>
        <p className="text-xs text-text-muted">
          {citationCount} citation{citationCount === 1 ? '' : 's'} marked
        </p>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-text-secondary border border-dashed border-border rounded-lg p-4">
          No user amendments affect citations on this site.
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <article
              key={row.id}
              className="border border-border rounded-lg p-3 text-sm space-y-2"
              data-testid={`audit-amendment-${row.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-text-primary">{row.label}</p>
                  <p className="text-xs text-text-muted">
                    {row.kind === 'sed' ? 'SED variable' : 'Report edit'} · {row.by} · {formatIsoDate(row.at)}
                  </p>
                </div>
                {'reportId' in row && row.reportId ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1 text-xs"
                    onClick={() => jumpToReport(row.reportId!)}
                    data-testid={`audit-amendment-jump-${row.id}`}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Jump
                  </Button>
                ) : null}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="bg-danger/10 border border-danger/30 rounded p-2">
                  <p className="font-semibold text-danger uppercase tracking-wider text-[10px]">Original</p>
                  <p className="text-text-muted mt-1 whitespace-pre-wrap">{truncate(row.original, 280)}</p>
                </div>
                <div className="bg-success/10 border border-success/30 rounded p-2">
                  <p className="font-semibold text-success uppercase tracking-wider text-[10px]">Amended</p>
                  <p className="text-text-secondary mt-1 whitespace-pre-wrap">{truncate(row.amended, 280)}</p>
                </div>
              </div>
              {row.rationale ? (
                <p className="text-xs text-text-secondary italic">{row.rationale}</p>
              ) : null}
              {row.citationsAffected.length > 0 ? (
                <p className="text-xs text-text-muted">
                  Citations affected: Ref {row.citationsAffected.join(', Ref ')}
                </p>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
